const LAB_ORDER_STORAGE_KEY = 'kis_rx_lab_orders_v1'
const LAB_ORDER_EVENT = 'kis-rx-lab-orders-changed'

export function isLocalLabOrder(order = {}) {
  return order.source === 'local_lab_order' || order.order_type === 'lab_order' || order.product?.category === 'lab'
}

export function labOrderHref(order = {}) {
  return isLocalLabOrder(order) ? '/labs' : `/orders/${order.id}`
}

export function panelPriceCents(price) {
  const match = String(price || '').replace(/,/g, '').match(/\$?\s*(\d+(?:\.\d{1,2})?)/)
  if (!match) return null
  const amount = Number(match[1])
  return Number.isFinite(amount) ? Math.round(amount * 100) : null
}

function sortedOrders(orders) {
  return [...orders].sort((a, b) => {
    const aTime = Date.parse(a.updated_at || a.admin_order_date || a.order_date || a.created_at || 0) || 0
    const bTime = Date.parse(b.updated_at || b.admin_order_date || b.order_date || b.created_at || 0) || 0
    return bTime - aTime
  })
}

export function readLocalLabOrders() {
  if (typeof window === 'undefined') return []
  try {
    const parsed = JSON.parse(window.localStorage.getItem(LAB_ORDER_STORAGE_KEY) || '[]')
    return sortedOrders(Array.isArray(parsed) ? parsed.filter(Boolean) : [])
  } catch {
    return []
  }
}

function writeLocalLabOrders(orders) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(LAB_ORDER_STORAGE_KEY, JSON.stringify(sortedOrders(orders).slice(0, 25)))
  window.dispatchEvent(new CustomEvent(LAB_ORDER_EVENT))
}

export function createLocalLabOrder({ panel, form }) {
  const now = new Date().toISOString()
  const directPriceCents = Number(panel?.msrpPriceCents ?? panel?.patientPriceCents ?? panel?.priceCents ?? 0)
  const priceCents = Number.isFinite(directPriceCents) && directPriceCents > 0
    ? directPriceCents
    : panelPriceCents(panel?.price)
  const order = {
    id: `lab-order-${Date.now()}`,
    source: 'local_lab_order',
    source_type: 'patient_lab_panel_request',
    order_type: 'lab_order',
    status: 'pending_intake',
    amount_charged_cents: priceCents,
    product_display_name: panel?.name || 'Lab panel',
    product_full_name: panel?.name || 'Lab panel',
    product: {
      id: panel?.id || null,
      display_name: panel?.name || 'Lab panel',
      category: 'lab',
      medication_name: 'Lab panel',
      patient_price_cents: priceCents,
    },
    pricing: {
      amount_cents: priceCents,
      one_time_cents: priceCents,
      payment_type: 'one_time',
      cadence: 'one_time',
      discount_percent: null,
      commitment_months: null,
    },
    lab_panel: {
      id: panel?.id || null,
      name: panel?.name || 'Lab panel',
      category: panel?.category || null,
      categoryLabel: panel?.categoryLabel || null,
      price: panel?.price || 'Pending',
      biomarkers: panel?.biomarkers || [],
    },
    metadata: {
      panel_id: panel?.id || null,
      panel_name: panel?.name || null,
      biomarker_count: panel?.biomarkers?.length || 0,
      biomarkers: panel?.biomarkers || [],
      goal: form?.goal || '',
      details: form?.details || '',
      state: form?.state || '',
      zip: form?.zip || '',
      collection_preference: form?.collectionPreference || '',
      intake_required_after_purchase: true,
      next_steps: [
        'Complete the intake form so goals, symptoms, and history are attached to the lab purchase.',
        'Confirm the requested collection method and eligible lab route.',
        'Release the requisition or concierge collection instructions after intake.',
        'Attach results to the Labs tab and unlock AI review when resulted.',
      ],
    },
    created_at: now,
    updated_at: now,
  }

  const existing = readLocalLabOrders().filter(item => item.id !== order.id)
  writeLocalLabOrders([order, ...existing])
  return order
}

export function mergeOrdersWithLocalLabs(orders = []) {
  const localOrders = readLocalLabOrders()
  const ids = new Set((orders || []).map(order => order.id))
  return sortedOrders([...(orders || []), ...localOrders.filter(order => !ids.has(order.id))])
}

